import { deleteData, getData, patchData, postData } from "../api/api";
import type { PageResponse } from "../types/dashboard";
import type {
  AnomalyBackendStatus,
  AnomalyDefinition,
  AnomalyIncident,
  AnomalyIncidentStatus,
  AnomalyMutationDraft,
  AnomalyPluginDetectorStatus,
  AnomalyRun,
} from "../types/anomaly";

const BASE = "/anomalies";

export const anomalyService = {
  backendStatus: () => getData(`${BASE}/backend-status`) as Promise<AnomalyBackendStatus>,

  listDefinitions: (page = 0, size = 20, search?: string) => {
    const q = new URLSearchParams({ page: String(page), size: String(size) });
    if (search) q.set("search", search);
    return getData(`${BASE}/definitions?${q}`) as Promise<PageResponse<AnomalyDefinition>>;
  },

  getDefinition: (id: string) =>
    getData(`${BASE}/definitions/${id}`) as Promise<AnomalyDefinition>,

  createDefinition: (body: AnomalyDefinition) =>
    postData(`${BASE}/definitions`, body) as Promise<AnomalyDefinition>,

  updateDefinition: (id: string, body: AnomalyDefinition) =>
    patchData(`${BASE}/definitions/${id}`, body) as Promise<AnomalyDefinition>,

  removeDefinition: (id: string) => deleteData(`${BASE}/definitions/${id}`),

  runNow: (id: string) =>
    postData(`${BASE}/definitions/${id}/run-now`, {}) as Promise<AnomalyRun>,

  pluginDetectorStatus: (id: string) =>
    getData(`${BASE}/definitions/${id}/plugin-detector/status`) as Promise<AnomalyPluginDetectorStatus>,

  listPluginDetectors: () =>
    getData(`${BASE}/plugin-detectors`) as Promise<AnomalyPluginDetectorStatus[]>,

  startOpenSearchDetector: (detectorId: string) =>
    postData(
      `${BASE}/plugin-detectors/${encodeURIComponent(detectorId)}/start`,
      {}
    ) as Promise<AnomalyPluginDetectorStatus>,

  stopOpenSearchDetector: (detectorId: string) =>
    postData(
      `${BASE}/plugin-detectors/${encodeURIComponent(detectorId)}/stop`,
      {}
    ) as Promise<AnomalyPluginDetectorStatus>,

  deleteOpenSearchDetector: (detectorId: string) =>
    deleteData(
      `${BASE}/plugin-detectors/${encodeURIComponent(detectorId)}`
    ) as Promise<AnomalyPluginDetectorStatus>,

  startPluginDetector: (id: string) =>
    postData(`${BASE}/definitions/${id}/plugin-detector/start`, {}) as Promise<AnomalyPluginDetectorStatus>,

  syncPluginDetector: (id: string) =>
    postData(`${BASE}/definitions/${id}/plugin-detector/sync`, {}) as Promise<AnomalyPluginDetectorStatus>,

  stopPluginDetector: (id: string) =>
    postData(`${BASE}/definitions/${id}/plugin-detector/stop`, {}) as Promise<AnomalyPluginDetectorStatus>,

  deletePluginDetector: (id: string) =>
    deleteData(`${BASE}/definitions/${id}/plugin-detector`) as Promise<AnomalyPluginDetectorStatus>,

  listIncidents: (params?: {
    page?: number;
    size?: number;
    status?: AnomalyIncidentStatus;
    definitionId?: string;
    search?: string;
  }) => {
    const q = new URLSearchParams({
      page: String(params?.page ?? 0),
      size: String(params?.size ?? 20),
    });
    if (params?.status) q.set("status", params.status);
    if (params?.definitionId) q.set("definitionId", params.definitionId);
    if (params?.search) q.set("search", params.search);
    return getData(`${BASE}/incidents?${q}`) as Promise<PageResponse<AnomalyIncident>>;
  },

  getIncident: (id: string) => getData(`${BASE}/incidents/${id}`) as Promise<AnomalyIncident>,

  patchIncident: (id: string, body: Partial<AnomalyIncident>) =>
    patchData(`${BASE}/incidents/${id}`, body) as Promise<AnomalyIncident>,

  listRuns: (params?: { page?: number; size?: number; definitionId?: string }) => {
    const q = new URLSearchParams({
      page: String(params?.page ?? 0),
      size: String(params?.size ?? 20),
    });
    if (params?.definitionId) q.set("definitionId", params.definitionId);
    return getData(`${BASE}/runs?${q}`) as Promise<PageResponse<AnomalyRun>>;
  },

  getDraft: (id: string) => getData(`${BASE}/drafts/${id}`) as Promise<AnomalyMutationDraft>,

  applyDraft: (id: string) =>
    postData(`${BASE}/drafts/${id}/apply`, {}) as Promise<AnomalyMutationDraft>,

  rejectDraft: (id: string) =>
    postData(`${BASE}/drafts/${id}/reject`, {}) as Promise<AnomalyMutationDraft>,
};
